// src/pages/admin/AdminDashboard.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Drawer,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  IconButton,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  TrendingUp,
  ShoppingCart,
  AttachMoney,
  Assignment,
  CheckCircle,
  Schedule,
  Receipt,
  Star,
  Business,
  Visibility,
  TrendingUp as TrendingUpIcon,
  Download,
  CalendarMonth,
  Refresh,
  PictureAsPdf,
  InsertDriveFile,
  MoreVert,
} from '@mui/icons-material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import '../../css/admin/admin.css';

import {
  useGetDashboardAnalyticsQuery,
  useGetMostViewedProductsQuery,
  useGetHighSaleProductsQuery,
  useDownloadMostViewedPDFMutation,
  useDownloadHighSalePDFMutation,
  useDownloadMostViewedCSVMutation,
  useDownloadHighSaleCSVMutation,
  RangeType,
  MostViewedProduct,
  HighSaleProduct,
} from '../../features/admin/dashboardApi.ts';

const RANGE_OPTIONS: { value: RangeType; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'custom', label: 'Custom' },
];

const PRODUCT_RANGE_OPTIONS = [
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' },
];

// Alert component for Snackbar
const AlertComponent = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref,
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const formatNumber = (value?: number | string) => {
  if (typeof value === 'number') {
    return value.toLocaleString('en-IN');
  }
  if (typeof value === 'string') {
    const num = parseFloat(value);
    return isNaN(num) ? value : num.toLocaleString('en-IN');
  }
  return '0';
};

const formatCurrency = (value?: number | string) => {
  if (typeof value === 'string') {
    const num = parseFloat(value);
    return isNaN(num) ? `${value} AED` : `${num.toLocaleString('en-IN')} AED`;
  }
  return `${formatNumber(value)} AED`;
};

const formatDecimal = (value?: number | string) => {
  if (typeof value === 'number') {
    return value.toFixed(2);
  }
  if (typeof value === 'string') {
    const num = parseFloat(value);
    return isNaN(num) ? value : num.toFixed(2);
  }
  return '0.00';
};

const calcPercent = (current?: number, last?: number): number | null => {
  if (last === undefined || last === 0 || current === undefined) return null;
  return ((current - last) / last) * 100;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminDashboard: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [rangeType, setRangeType] = useState<RangeType>('last_week');
  const [productTab, setProductTab] = useState(0);
  const [productRange, setProductRange] = useState<'week' | 'month' | 'year' | 'custom'>('week');
  const [customDateDialog, setCustomDateDialog] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [downloading, setDownloading] = useState(false);

  // Dashboard analytics query
  const { 
    data, 
    error: dashboardError, 
    isLoading: dashboardLoading, 
    isFetching: dashboardFetching,
    refetch: refetchDashboard 
  } = useGetDashboardAnalyticsQuery(rangeType);

  // Product analytics queries
  const {
    data: mostViewedData,
    isLoading: mostViewedLoading,
    error: mostViewedError,
    refetch: refetchMostViewed,
  } = useGetMostViewedProductsQuery({
    range: productRange,
    ...(productRange === 'custom' && fromDate && toDate ? { from_date: fromDate, to_date: toDate } : {}),
    pdf: 0,
    csv: 0,
  });

  const {
    data: highSaleData,
    isLoading: highSaleLoading,
    error: highSaleError,
    refetch: refetchHighSale,
  } = useGetHighSaleProductsQuery({
    range: productRange,
    ...(productRange === 'custom' && fromDate && toDate ? { from_date: fromDate, to_date: toDate } : {}),
    pdf: 0,
    csv: 0,
  });

  // Download mutations
  const [downloadMostViewedPDF] = useDownloadMostViewedPDFMutation();
  const [downloadHighSalePDF] = useDownloadHighSalePDFMutation();
  const [downloadMostViewedCSV] = useDownloadMostViewedCSVMutation();
  const [downloadHighSaleCSV] = useDownloadHighSaleCSVMutation();

  useEffect(() => {
    // Reset dates when range changes
    if (productRange !== 'custom') {
      setFromDate('');
      setToDate('');
    }
  }, [productRange]);

  const handleToggleMenu = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleRangeChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    const newRange = event.target.value as RangeType;
    setRangeType(newRange);
  };

  const handleProductRangeChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    const value = event.target.value as 'week' | 'month' | 'year' | 'custom';
    setProductRange(value);
    if (value === 'custom') {
      setCustomDateDialog(true);
    }
  };

  const handleProductTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setProductTab(newValue);
  };

  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = async (type: 'pdf' | 'csv') => {
    try {
      setDownloading(true);
      const params = {
        range: productRange,
        ...(productRange === 'custom' && fromDate && toDate ? { from_date: fromDate, to_date: toDate } : {}),
      };

      let result: any;
      
      if (productTab === 0) {
        // Most Viewed Products
        if (type === 'pdf') {
          result = await downloadMostViewedPDF(params).unwrap();
        } else {
          const blob = await downloadMostViewedCSV(params).unwrap();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `most-viewed-products-${productRange}-${new Date().toISOString().split('T')[0]}.${type}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      } else {
        // High Sale Products
        if (type === 'pdf') {
          result = await downloadHighSalePDF(params).unwrap();
        } else {
          const blob = await downloadHighSaleCSV(params).unwrap();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `high-sale-products-${productRange}-${new Date().toISOString().split('T')[0]}.${type}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      }

      // For PDF downloads, open the URL
      if (type === 'pdf' && result?.download_url) {
        window.open(result.download_url, '_blank');
      }

      setSnackbar({
        open: true,
        message: type === 'pdf' 
          ? 'PDF generated successfully! Opening download...' 
          : 'CSV downloaded successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error(`Error downloading ${type.toUpperCase()}:`, error);
      setSnackbar({
        open: true,
        message: `Failed to download ${type.toUpperCase()}. Please try again.`,
        severity: 'error'
      });
    } finally {
      setDownloading(false);
      handleDownloadMenuClose();
    }
  };

  const handleApplyCustomDates = () => {
    if (fromDate && toDate) {
      if (productTab === 0) {
        refetchMostViewed();
      } else {
        refetchHighSale();
      }
      setCustomDateDialog(false);
      setSnackbar({
        open: true,
        message: `Filter applied: ${fromDate} to ${toDate}`,
        severity: 'info'
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Please select both from and to dates',
        severity: 'warning'
      });
    }
  };

  const handleRefreshData = () => {
    if (productTab === 0) {
      refetchMostViewed();
    } else {
      refetchHighSale();
    }
    setSnackbar({
      open: true,
      message: 'Data refreshed!',
      severity: 'info'
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Safely read values from API
  const dailyTotalSales = data?.daily_sales?.total?.sales ?? 0;
  const dailyTotalOrders = data?.daily_sales?.total?.orders ?? 0;

  const weeklyNewUsersCurrent = data?.new_users?.total?.current_week ?? 0;
  const weeklyNewUsersLast = data?.new_users?.total?.last_week ?? 0;
  const weeklyNewUsersPercent = calcPercent(
    weeklyNewUsersCurrent,
    weeklyNewUsersLast
  );

  const rangeSalesCurrent = data?.range?.range_summary?.total?.sales ?? 0;
  const rangeSalesPercent =
    data?.range?.comparison?.total?.sales?.percent ?? null;

  const monthCurrent = data?.month_growth?.total?.current ?? 0;
  const monthLast = data?.month_growth?.total?.last ?? 0;
  const monthPercent = calcPercent(monthCurrent, monthLast);

  const quarterCurrent = data?.quarter_growth?.total?.current ?? 0;
  const quarterLast = data?.quarter_growth?.total?.last ?? 0;
  const quarterPercent = calcPercent(quarterCurrent, quarterLast);

  // Current data based on active tab
  const currentData = productTab === 0 
    ? mostViewedData?.data || [] 
    : highSaleData?.data || [];

  // Table headers based on active tab
  const mostViewedHeaders = [
    { key: 'product_id', label: 'Product ID' },
    { key: 'product_name', label: 'Product Name' },
    { key: 'price', label: 'Price (AED)' },
    { key: 'total_views', label: 'Total Views' },
    { key: 'total_viewers', label: 'Total Viewers' },
  ];

  const highSaleHeaders = [
    { key: 'product_id', label: 'Product ID' },
    { key: 'product_name', label: 'Product Name' },
    { key: 'price', label: 'Price (AED)' },
    { key: 'total_qty', label: 'Total Quantity' },
    { key: 'total_buyers', label: 'Total Buyers' },
    { key: 'avg_sale_per_day', label: 'Avg Sale/Day' },
  ];

  const currentHeaders = productTab === 0 ? mostViewedHeaders : highSaleHeaders;

  // 4 top stat cards (now dynamic)
  const statsCards = [
    {
      title: "Today's Total Sales",
      value: formatCurrency(dailyTotalSales),
      icon: <Assignment />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      progress: 100,
    },
    {
      title: "Today's Total Orders",
      value: formatNumber(dailyTotalOrders),
      icon: <CheckCircle />,
      gradient: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
      progress: 100,
    },
    {
      title: 'New Users (This Week)',
      value: formatNumber(weeklyNewUsersCurrent),
      icon: <Schedule />,
      gradient: 'linear-gradient(135deg, #FF9800 0%, #FFC107 100%)',
      progress: 100,
    },
    {
      title: 'Change vs Last Week (Users)',
      value: `${
        weeklyNewUsersPercent !== null
          ? (weeklyNewUsersPercent > 0 ? '+' : '') + weeklyNewUsersPercent.toFixed(1)
          : '0'
      }%`,
      icon: <Receipt />,
      gradient: weeklyNewUsersPercent !== null && weeklyNewUsersPercent >= 0
        ? 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)'
        : 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)',
      progress: 100,
    },
  ];

  // 3 sales cards (dynamic)
  const salesCards = [
    {
      title: 'Current Range Sales',
      value: formatCurrency(rangeSalesCurrent),
      subtitle: 'Selected period total sales',
      icon: <TrendingUp />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      change:
        rangeSalesPercent !== null
          ? (rangeSalesPercent > 0 ? '+' : '') + rangeSalesPercent.toFixed(1) + '%'
          : '0%',
      changeColor: rangeSalesPercent !== null && rangeSalesPercent >= 0 ? '#4CAF50' : '#FF6B6B'
    },
    {
      title: 'Monthly Revenue',
      value: formatCurrency(monthCurrent),
      subtitle: `Last Month: ${formatCurrency(monthLast)}`,
      icon: <AttachMoney />,
      gradient: 'linear-gradient(135deg, #9C27B0 0%, #E91E63 100%)',
      change:
        monthPercent !== null ? (monthPercent > 0 ? '+' : '') + monthPercent.toFixed(1) + '%' : '0%',
      changeColor: monthPercent !== null && monthPercent >= 0 ? '#4CAF50' : '#FF6B6B'
    },
    {
      title: 'Quarter Revenue',
      value: formatCurrency(quarterCurrent),
      subtitle: `Last Quarter: ${formatCurrency(quarterLast)}`,
      icon: <ShoppingCart />,
      gradient: 'linear-gradient(135deg, #2196F3 0%, #03A9F4 100%)',
      change:
        quarterPercent !== null
          ? (quarterPercent > 0 ? '+' : '') + quarterPercent.toFixed(1) + '%'
          : '0%',
      changeColor: quarterPercent !== null && quarterPercent >= 0 ? '#4CAF50' : '#FF6B6B'
    },
  ];

  const topProducts =
    data?.top_products?.slice(0, 5).map((p: any, index: number) => ({
      name: `Product #${p.product_id}`,
      quantity: p.total_quantity,
      value: p.total_value,
      rank: index + 1,
    })) ?? [];

  const currentRangeLabel =
    RANGE_OPTIONS.find((r) => r.value === rangeType)?.label || '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <AlertComponent onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </AlertComponent>
      </Snackbar>

      {/* Download Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleDownloadMenuClose}
      >
        <MenuItem onClick={() => handleDownload('pdf')} disabled={downloading}>
          <ListItemIcon>
            <PictureAsPdf fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDownload('csv')} disabled={downloading}>
          <ListItemIcon>
            <InsertDriveFile fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download CSV</ListItemText>
        </MenuItem>
      </Menu>

      {/* Custom Date Dialog */}
      <Dialog open={customDateDialog} onClose={() => setCustomDateDialog(false)}>
        <DialogTitle>Select Custom Date Range</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, minWidth: 300 }}>
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDateDialog(false)}>Cancel</Button>
          <Button onClick={handleApplyCustomDates} variant="contained" color="primary">
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Row: Sidebar + Main Content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Desktop Sidebar */}
        <Box
          sx={{
            width: '280px',
            flexShrink: 0,
            overflowY: 'auto',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Slidemenu
            mobileOpen={false}
            onToggle={function (): void {
              throw new Error('Function not implemented.');
            }}
          />
        </Box>

        {/* Mobile Sidebar */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleToggleMenu}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu
            mobileOpen={false}
            onToggle={function (): void {
              throw new Error('Function not implemented.');
            }}
          />
        </Drawer>

        {/* Main Content area with Header + Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ flexShrink: 0 }}>
            <AdminHeader onToggleMenu={handleToggleMenu} />
          </Box>

          {/* Main Content */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              padding: { xs: 1, sm: 2 },
              paddingTop: '10px',
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              minHeight: '100vh',
            }}
          >
            <Box
              sx={{
                padding: { xs: 1, sm: 3 },
                maxWidth: '1400px',
                margin: '0 auto',
              }}
            >
              {/* Dashboard Header + Range Filter */}
              <Box
                sx={{
                  mb: 4,
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 3,
                  padding: 3,
                  color: 'white',
                  boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: { xs: '28px', sm: '36px' },
                      mb: 1,
                    }}
                  >
                    📊 Admin Dashboard
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {`Overview for: ${currentRangeLabel}`}
                  </Typography>
                </Box>

                <FormControl
                  size="small"
                  sx={{ minWidth: 200, backgroundColor: 'white', borderRadius: 2 }}
                >
                  <InputLabel id="range-type-label"></InputLabel>
                  <Select
                    labelId="range-type-label"
                    value={rangeType}
                    label=""
                    onChange={handleRangeChange}
                  >
                    {RANGE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Loading / Error State for Dashboard */}
              {dashboardLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 5,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}

              {dashboardError && !dashboardLoading && (
                <Box sx={{ mb: 3 }}>
                  <Alert severity="error">
                    Failed to load dashboard analytics. Please check your connection.
                  </Alert>
                </Box>
              )}

              {!dashboardLoading && (
                <>
                  {dashboardFetching && (
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress />
                    </Box>
                  )}

                  {/* Stats Cards Row (4) */}
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    {statsCards.map((card, index) => (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card
                          sx={{
                            background: card.gradient,
                            color: 'white',
                            borderRadius: 3,
                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                            transition: 'transform 0.3s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-5px)',
                              boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                            },
                            height: '100%',
                          }}
                        >
                          <CardContent sx={{ p: 3, height: '100%' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.2)',
                                  mr: 2,
                                  width: 50,
                                  height: 50,
                                }}
                              >
                                {card.icon}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="h4"
                                  sx={{ fontWeight: 'bold' }}
                                >
                                  {card.value}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ opacity: 0.9 }}
                                >
                                  {card.title}
                                </Typography>
                              </Box>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={card.progress}
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: 'rgba(255,255,255,0.8)',
                                },
                              }}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Sales Cards Row (3) */}
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    {salesCards.map((card, index) => (
                      <Grid item xs={12} md={4} key={index}>
                        <Card
                          sx={{
                            background: card.gradient,
                            color: 'white',
                            borderRadius: 3,
                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                            height: '100%',
                            transition: 'transform 0.3s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-5px)',
                              boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                            },
                          }}
                        >
                          <CardContent
                            sx={{
                              p: 3,
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 2,
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.2)',
                                  width: 45,
                                  height: 45,
                                }}
                              >
                                {card.icon}
                              </Avatar>
                              <Chip
                                label={card.change}
                                size="small"
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.2)',
                                  color: card.changeColor || 'white',
                                  fontWeight: 'bold',
                                  borderColor: card.changeColor,
                                  borderWidth: 1,
                                  borderStyle: 'solid',
                                }}
                              />
                            </Box>
                            <Typography
                              variant="h3"
                              sx={{ fontWeight: 'bold', mb: 1 }}
                            >
                              {card.value}
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ opacity: 0.9, mb: 1 }}
                            >
                              {card.title}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                              {card.subtitle}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Product Analytics Section */}
                  <Card sx={{ mb: 4 }}>
                    <CardContent sx={{ p: 0 }}>
                      <Box
                        sx={{
                          background: 'linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)',
                          p: 3,
                          borderTopLeftRadius: 8,
                          borderTopRightRadius: 8,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                            Product Analytics
                          </Typography>
                          <FormControl
                            size="small"
                            sx={{ minWidth: 200, backgroundColor: 'white', borderRadius: 2 }}
                          >
                            <InputLabel id="product-range-label"></InputLabel>
                            <Select
                              labelId="product-range-label"
                              value={productRange}
                              label=""
                              onChange={handleProductRangeChange}
                            >
                              {PRODUCT_RANGE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {productRange === 'custom' && fromDate && toDate && (
                            <Chip
                              icon={<CalendarMonth />}
                              label={`${fromDate} to ${toDate}`}
                              size="small"
                              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                            />
                          )}
                          <Tooltip title="Refresh Data">
                            <IconButton 
                              onClick={handleRefreshData}
                              sx={{ color: 'white' }}
                            >
                              <Refresh />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            startIcon={<Download />}
                            onClick={handleDownloadMenuOpen}
                            disabled={downloading}
                            sx={{
                              backgroundColor: 'white',
                              color: '#2196F3',
                              '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.9)',
                              },
                            }}
                          >
                            {downloading ? 'Downloading...' : 'Download'}
                          </Button>
                          <IconButton
                            onClick={handleDownloadMenuOpen}
                            sx={{ color: 'white' }}
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                      </Box>

                      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={productTab} onChange={handleProductTabChange}>
                          <Tab
                            icon={<Visibility />}
                            iconPosition="start"
                            label={`Most Viewed Products (${mostViewedData?.total || 0})`}
                            sx={{ fontWeight: 'bold' }}
                          />
                          <Tab
                            icon={<TrendingUpIcon />}
                            iconPosition="start"
                            label={`High Sale Products (${highSaleData?.total || 0})`}
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Tabs>
                      </Box>

                      {/* Most Viewed Products Tab */}
                      <TabPanel value={productTab} index={0}>
                        {mostViewedLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                            <CircularProgress />
                          </Box>
                        ) : mostViewedError ? (
                          <Alert severity="error">
                            Failed to load most viewed products. {mostViewedError.toString()}
                          </Alert>
                        ) : (
                          <Box>
                            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary">
                                Showing {mostViewedData?.data?.length || 0} products
                                {mostViewedData?.total && mostViewedData.total > (mostViewedData?.data?.length || 0) 
                                  ? ` of ${mostViewedData.total}` 
                                  : ''}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Data fetched: {new Date().toLocaleTimeString()}
                              </Typography>
                            </Box>
                            <TableContainer 
                              component={Paper} 
                              sx={{ 
                                maxHeight: 500,
                                '& .MuiTableCell-head': {
                                  fontWeight: 'bold',
                                  backgroundColor: '#f5f5f5',
                                }
                              }}
                            >
                              <Table stickyHeader size="small">
                                <TableHead>
                                  <TableRow>
                                    {currentHeaders.map((header) => (
                                      <TableCell key={header.key}>
                                        {header.label}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {currentData.map((product: any, index: number) => (
                                    <TableRow 
                                      key={product.product_id || index}
                                      sx={{ 
                                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                                        '&:hover': { backgroundColor: '#f0f0f0' }
                                      }}
                                    >
                                      <TableCell>{product.product_id}</TableCell>
                                      <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                          {product.product_name}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip 
                                          label={formatCurrency(product.price)} 
                                          size="small" 
                                          color="primary"
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Visibility fontSize="small" color="action" />
                                          <Typography>{formatNumber(product.total_views)}</Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Chip 
                                          label={formatNumber(product.total_viewers)} 
                                          size="small" 
                                          color="secondary"
                                          variant="outlined"
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {currentData.length === 0 && (
                                    <TableRow>
                                      <TableCell 
                                        colSpan={currentHeaders.length} 
                                        align="center"
                                        sx={{ py: 4 }}
                                      >
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                          <Typography color="text.secondary">
                                            No data available
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            Try changing the time range or check your API connection
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            {mostViewedData && (
                              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong> Total Records:</strong> {mostViewedData.total} | 
                                  <strong> Response Time:</strong> {new Date().toLocaleTimeString()}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </TabPanel>

                      {/* High Sale Products Tab */}
                      <TabPanel value={productTab} index={1}>
                        {highSaleLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                            <CircularProgress />
                          </Box>
                        ) : highSaleError ? (
                          <Alert severity="error">
                            Failed to load high sale products. {highSaleError.toString()}
                          </Alert>
                        ) : (
                          <Box>
                            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary">
                                Showing {highSaleData?.data?.length || 0} products
                                {highSaleData?.total && highSaleData.total > (highSaleData?.data?.length || 0) 
                                  ? ` of ${highSaleData.total}` 
                                  : ''}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Data fetched: {new Date().toLocaleTimeString()}
                              </Typography>
                            </Box>
                            <TableContainer 
                              component={Paper} 
                              sx={{ 
                                maxHeight: 500,
                                '& .MuiTableCell-head': {
                                  fontWeight: 'bold',
                                  backgroundColor: '#f5f5f5',
                                }
                              }}
                            >
                              <Table stickyHeader size="small">
                                <TableHead>
                                  <TableRow>
                                    {currentHeaders.map((header) => (
                                      <TableCell key={header.key}>
                                        {header.label}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {currentData.map((product: any, index: number) => (
                                    <TableRow 
                                      key={product.product_id || index}
                                      sx={{ 
                                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                                        '&:hover': { backgroundColor: '#f0f0f0' }
                                      }}
                                    >
                                      <TableCell>{product.product_id}</TableCell>
                                      <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                          {product.product_name}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip 
                                          label={formatCurrency(product.price)} 
                                          size="small" 
                                          color="primary"
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <ShoppingCart fontSize="small" color="action" />
                                          <Typography>{formatNumber(product.total_qty)}</Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Chip 
                                          label={formatNumber(product.total_buyers)} 
                                          size="small" 
                                          color="secondary"
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Chip 
                                          label={formatDecimal(product.avg_sale_per_day)} 
                                          size="small" 
                                          color="success"
                                          variant="outlined"
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {currentData.length === 0 && (
                                    <TableRow>
                                      <TableCell 
                                        colSpan={currentHeaders.length} 
                                        align="center"
                                        sx={{ py: 4 }}
                                      >
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                          <Typography color="text.secondary">
                                            No data available
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            Try changing the time range or check your API connection
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            {highSaleData && (
                              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>API Status:</strong> {highSaleData.error ? 'Error' : 'Success'} | 
                                  <strong> Total Records:</strong> {highSaleData.total} | 
                                  <strong> Response Time:</strong> {new Date().toLocaleTimeString()}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </TabPanel>
                    </CardContent>
                  </Card>

                  {/* Additional Analytics Row */}
                  <Grid container spacing={3}>
                    {/* Top Products */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUpIcon color="primary" /> Top Products
                          </Typography>
                          {topProducts.length > 0 ? (
                            topProducts.map((product) => (
                              <Box key={product.rank} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                <Avatar 
                                  sx={{ 
                                    bgcolor: product.rank === 1 ? '#FFD700' : 
                                             product.rank === 2 ? '#C0C0C0' : 
                                             product.rank === 3 ? '#CD7F32' : '#667eea',
                                    color: 'white',
                                    mr: 2,
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {product.rank}
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="body1" fontWeight="medium">
                                    {product.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Quantity: {formatNumber(product.quantity)} | Value: {formatCurrency(product.value)}
                                  </Typography>
                                </Box>
                              </Box>
                            ))
                          ) : (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                              <Typography color="text.secondary">
                                No top products data available
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Quick Stats */}
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business color="primary" /> Quick Stats
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                <Typography variant="h4" color="primary" fontWeight="bold">
                                  {formatNumber(weeklyNewUsersCurrent)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  New Users (Week)
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                <Typography variant="h4" color="secondary" fontWeight="bold">
                                  {formatNumber(dailyTotalOrders)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Today's Orders
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                <Typography variant="h4" color="success" fontWeight="bold">
                                  {formatCurrency(monthCurrent)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Monthly Revenue
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                <Typography variant="h4" color="warning" fontWeight="bold">
                                  {formatCurrency(quarterCurrent)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Quarter Revenue
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDashboard;